import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CalendarDays, Clock, CheckCircle2 } from "lucide-react";
import { useLang } from "@/i18n";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";

const SLOTS = ["09:00", "10:00", "11:00", "13:30", "14:30", "15:30", "16:30", "17:30"];

export const Booking = () => {
  const { t, lang } = useLang();
  const b = t.booking;
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [booked, setBooked] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", sector: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const fmt = (d) => d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "";

  useEffect(() => {
    if (!date) return;
    setTime("");
    api.get(`/appointments/booked?date=${fmt(date)}`).then((r) => setBooked(r.data.booked || [])).catch(() => setBooked([]));
  }, [date]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!date || !time || !form.name || !form.email || !form.phone) { toast.error(b.noSlots); return; }
    setSubmitting(true);
    try {
      await api.post("/appointments", { ...form, date: fmt(date), time, lang });
      setDone(true); toast.success(b.success);
    } catch (e) { toast.error("Erreur"); } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <section id="booking" className="py-24 sm:py-32 max-w-2xl mx-auto px-5 sm:px-8" data-testid="booking-success">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="glow-border glass rounded-2xl p-10 text-center accent-glow">
          <CheckCircle2 className="w-16 h-16 text-accent mx-auto mb-5" strokeWidth={1.5} />
          <h3 className="font-display text-2xl font-bold">{b.success}</h3>
          <p className="mt-3 text-muted-foreground">{fmt(date)} · {time}</p>
        </motion.div>
      </section>
    );
  }

  return (
    <section id="booking" className="relative py-24 sm:py-32 bg-[#0d2138] border-y border-white/5" data-testid="booking">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="mb-12 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-accent/10 mb-5"><CalendarDays className="w-6 h-6 text-accent" strokeWidth={1.5} /></div>
          <h2 className="font-display text-3xl sm:text-5xl font-bold">{b.title}</h2>
          <p className="mt-4 text-muted-foreground text-lg">{b.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="glass rounded-2xl p-6" data-testid="booking-calendar">
            <Calendar mode="single" selected={date} onSelect={setDate}
              disabled={(d) => { const day = d.getDay(); return d < new Date(new Date().setHours(0, 0, 0, 0)) || day === 0 || day === 6; }}
              className="rounded-md" />
            <div className="mt-5">
              <p className="text-sm text-muted-foreground mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-accent" strokeWidth={1.5} /> {b.time}</p>
              {!date ? <p className="text-sm text-muted-foreground/60">{b.pickDate}</p> : (
                <div className="grid grid-cols-4 gap-2">
                  {SLOTS.map((s) => {
                    const taken = booked.includes(s);
                    return (
                      <button key={s} disabled={taken} onClick={() => setTime(s)} data-testid={`slot-${s}`}
                        className={`text-sm rounded-lg py-2 border transition-colors ${taken ? "opacity-30 line-through cursor-not-allowed border-white/10" : time === s ? "border-accent bg-accent text-white" : "border-white/10 hover:border-accent"}`}>{s}</button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="glass rounded-2xl p-6 space-y-4" data-testid="booking-form">
            <Input placeholder={b.name + " *"} value={form.name} onChange={set("name")} data-testid="booking-name" className="bg-white/5 border-white/10 focus-visible:ring-accent" />
            <Input type="email" placeholder={b.email + " *"} value={form.email} onChange={set("email")} data-testid="booking-email" className="bg-white/5 border-white/10 focus-visible:ring-accent" />
            <Input placeholder={b.phone + " *"} value={form.phone} onChange={set("phone")} data-testid="booking-phone" className="bg-white/5 border-white/10 focus-visible:ring-accent" />
            <Input placeholder={b.sector} value={form.sector} onChange={set("sector")} data-testid="booking-sector" className="bg-white/5 border-white/10 focus-visible:ring-accent" />
            <Input placeholder={b.message} value={form.message} onChange={set("message")} data-testid="booking-message" className="bg-white/5 border-white/10 focus-visible:ring-accent" />
            {date && time && (
              <div className="rounded-xl bg-accent/10 border border-accent/30 p-3 text-sm text-center" data-testid="booking-selected">
                {fmt(date)} · {time}
              </div>
            )}
            <Button onClick={submit} disabled={submitting} data-testid="booking-submit"
              className="w-full rounded-full bg-white text-black hover:bg-accent hover:text-white transition-colors h-12 font-medium">
              {submitting ? b.submitting : b.submit}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
